export type BackupId = number;
export type RestoreTaskId = number;
export type RestoreDatabaseId = number;
export type RestoreWebsiteId = number;
export type RestoreServerId = number;

export enum BackupType {
  FILE = "file",
  MYSQL = "mysql",
  FULL = "full",
}

export enum TaskProgressStatus {
  Pending = 0,
  Running = 1,
  Finished = 2,
  Failed = 3,
}

export enum RestoreTaskType {
  ServerAndConfig = 0,
  ServerOnly = 1,
  ConfigOnly = 2,
}

export enum BackupRestoreType {
  WEBSITE = "website",
  MYSQL = "mysql",
}

export enum BackupScope {
  SERVER = "server",
}

export enum BackupEndStatus {
  PENDING = "pending",
  SUCCESS = "success",
  FAILED = "failed",
  PARTIAL = "partial",
}

export type RestoreBackupOptionsDto = {
  restoreConfigTypes?: string[];
  restoreBackupTargets?: number[];
};

export type WebsiteRestoreBackupDto = {
  targetBackupId: number;
  websiteBackupId: number;
  config: object,
  targets: {
    id: number;
    type: string;
    uniqueIdentifier: string;
  }[],
  options?: RestoreBackupOptionsDto;
}

export type WebsiteGroupRestoreBackupDto = {
  targetBackupId: number;
  websiteGroupId: number;
}

export type WebsiteGroupRestoreBackupDtoExtended = WebsiteGroupRestoreBackupDto & {
  targetBackupId: number;
  websiteGroupId: number;
  mainWebsiteDomain: string;
  scope: string;
}

export type RestoreBackupDto = WebsiteRestoreBackupDto;

export type BackupSummaryDto = {
  id: BackupId;
  repositoryId: number;
  scope: BackupScope;
  status: BackupEndStatus;
  createdAt: Date;
};

export type RestoreDatabase = {
  id: RestoreDatabaseId;
  status: TaskProgressStatus;
}

export type RestoreWebsite = {
  id: RestoreWebsiteId;
  status: TaskProgressStatus;
}

export type RestoreServer = {
  id: RestoreServerId;
  status: TaskProgressStatus;
}

export type RestoreTaskSummaryDto = {
  id: RestoreTaskId;
  type: RestoreTaskType;
  status: TaskProgressStatus;
  startDate: Date;
  endDate: Date;
  backup: BackupSummaryDto;
  websiteRestores: { websiteId: number, restoreConfigsJson: string, websiteRestoreTargets: WebsiteRestoreBackupTarget[] }[]
};


export type WebsiteRestoreBackupTarget = {
  id: number;
  status: TaskProgressStatus;
  restoreWebsiteId: number;
  restoreBackupTargetId: number;
  restoreBackupTarget?: WebsiteBackupTarget;
}

export type DatabaseBackupSummaryDto = {
  id: DatabaseBackupId;
  websiteId: number;
  databaseId: number;
  databaseName: string;
};

export type DatabaseBackupId = number;
export type DatabaseBackupConfigDto = unknown;

export type WebsiteBackupTarget = {
  id: number;
  type: string;
  uniqueIdentifier: string;
  snapshotTargetType?: string;
  snapshotTarget?: string;
}

export type WebsiteBackupSummaryDto = {
  id: WebsiteBackupId;
  backupId: number;
  websiteId: number;
  websiteGroupId: number;
  isMain: boolean;
  domain: string;
  configJson?: string;
  status: string;
  websiteBackupTargets: WebsiteBackupTarget[];
};

export type WebsiteBackupId = number;
export type WebsiteBackupConfigDto = unknown;

export type BackupSnapshotDto = {
  id: number;
  type: string;
  status: string;
  snapshotId: string;
}

export type BackupDto = {
  id: BackupId;
  backupTaskId: number;
  repositoryId: number;
  createdAt: Date;
  scope: BackupScope;
  status: BackupEndStatus;
  snapshots: BackupSnapshotDto[];
  repo: BackupRepoDto & {
    targetOriginServer: { originServerHostname: string, originServerId: string },
    backupServer: BackupServerDto
  }
};

export type BackupScheduleId = number;

export type CreateTaskScheduleDto = {
  type: string,
  targetServerId: string;
  intervalDays: number;
  runAtHour: number;
  runAtMinute: number;
  retries: number;
};

export type CreateInstantTaskDto = {
  type: string,
  targetServerId: string;
};

export type CreateInstantForgetRepositoryDto = {
  backupServerId: BackupServerId;
  repositoryIds: BackupRepoId[];
  retentionDays: number;
};

export type CreateInstantPruneRepositoryDto = {
  backupServerId: BackupServerId;
  repositoryIds: BackupRepoId[];
};

export type CreateSchedulePruneRepositoryDto = {
  backupServerId: BackupServerId;
  repositoryIds: BackupRepoId[];
  intervalDays: number;
  runAtHour: number;
  runAtMinute: number;
  retries: number;
};

export type CreateScheduledForgetRepositoryDto = {
  backupServerId: BackupServerId;
  repositoryIds: BackupRepoId[];
  intervalDays: number;
  runAtHour: number;
  runAtMinute: number;
  retries: number;
  retentionDays: number;
};

export enum BackupTaskStatus {
  PENDING = 0,
  RUNNING = 1,
  COMPLETED = 2,
  FAILED = 3,
  PARTIAL = 4,
}

export type BackupTaskId = number;

export type QueryResultDto<TData> = {
  data: TData[];
  total: number;
};

export type BackupTaskSummaryDto = {
  id: BackupTaskId;
  scheduleId: BackupScheduleId;
  repo: BackupRepoSummaryDto;
  status: BackupTaskStatus;
  runDate: Date;
  retriesLeft: number;
  createdAt: Date;
};

export type BackupTaskDto = {
  id: BackupTaskId;
  schedule: BackupScheduleDto;
  status: BackupTaskStatus;
  runDate: Date;
  retriesLeft: number;
  startDate: Date | null;
  endDate: Date | null;
  error: string | null;
  createdAt: Date;
  backups: BackupDto[];
};

export interface BackupTaskRepositoryInterface {
  getBackupTask(id: BackupTaskId): Promise<BackupTaskDto | null>;

  searchBackupTasks(query: PagingOptions): Promise<QueryResultDto<BackupTaskSummaryDto>>;
}

export type BackupScheduleDto = {
  id: BackupScheduleId;
  repositoryId: BackupRepoId;
  intervalDays: number;
  runAtHour: number;
  runAtMinute: number;
  lastRunDay: Date;
  retries: number;
  createdAt: Date;
};

export interface BackupRepositoryInterface {
  createTerminatedBackup(websiteGroupId: number): Promise<void>;

  getBackup(id: BackupId): Promise<BackupDto | null>;

  searchBackups(serverId: string | undefined, query: PagingOptions): Promise<QueryResultDto<BackupSummaryDto>>;

  restoreBackup(data: RestoreBackupDto): Promise<void>;

  restoreWebsiteGroupBackup(data: WebsiteGroupRestoreBackupDto): Promise<void>;

  deleteBackup(id: BackupId): Promise<void>;

  getWebsiteBackups(
    id: BackupId,
    websiteId: number | undefined,
    websiteDomain: string | undefined,
    websiteGroupId: number | undefined,
    options: PagingOptions | undefined,
  ): Promise<QueryResultDto<WebsiteBackupSummaryDto>>;

  getWebsiteBackup(backupId: number, websiteId: number): Promise<WebsiteBackupSummaryDto>;

  getDatabaseBackups(id: BackupId, options: PagingOptions): Promise<QueryResultDto<DatabaseBackupSummaryDto>>;

  getDatabaseBackupConfig(id: DatabaseBackupId): Promise<DatabaseBackupConfigDto>;
}

export interface RestoreTaskRepositoryInterface {
  searchRestoreTasks(serverId: string | undefined, websiteId: number | undefined, query: PagingOptions): Promise<QueryResultDto<RestoreTaskSummaryDto>>;

  getWebsiteRestoreData(id: RestoreTaskId): any;

  getDatabaseRestoreData(id: RestoreTaskId): any;

  getServerRestoreData(id: RestoreTaskId): any;

  getRestoreTask(id: RestoreTaskId): Promise<RestoreTaskSummaryDto>;
}

export type BackupRepoId = number;
export type BackupRepoDto = {
  id: BackupRepoId;
  repositoryId: string;
  repositoryPasswordPath: string;
  repositoryPath: string;
  backupServerId: string;
  targetOriginServerId?: string;
  targetOriginServerName?: string;
};

export type BackupRepoSecretsDto = BackupRepoDto & {
  repositoryPasswordRaw: string;
};

export type BackupRepoSummaryDto = {
  id: BackupRepoId;
  originServerId: string;
  originServerHostname: string;
}

export type CreateBackupRepoDTO = {
  repositoryId: string;
  repositoryPath?: string;
  repositoryPasswordPath?: string;
  repositoryPassword?: string;
  originServerHostname: string;
  originServerId: string;
  backupServer: number;
  retentionDays: number;
}

export interface BackupRepoRepositoryInterface {
  createBackupRepo(data: CreateBackupRepoDTO): Promise<void>;

  getAllBackupRepos(query: PagingWithSearchOptions): Promise<QueryResultDto<BackupRepoDto> | null>;

  getBackupRepo(id: BackupRepoId): Promise<BackupRepoDto | null>;

  getBackupRepoSecrets(id: BackupRepoId): Promise<BackupRepoSecretsDto | null>;

  deleteBackupRepo(id: BackupRepoId): Promise<void>;
}

export interface BackupScheduleRepositoryInterface {
  getBackupSchedule(id: BackupScheduleId): Promise<BackupScheduleDto | null>;

  searchBackupSchedules(query: PagingOptions): Promise<QueryResultDto<BackupScheduleDto>>;

  updateBackupSchedule(backupSchedule: BackupScheduleDto): Promise<void>;

  deleteBackupSchedule(id: BackupScheduleId): Promise<void>;
}

export interface TaskScheduleRepositoryInterface {
  searchTaskSchedules(type?: string, query?: PagingOptions): Promise<QueryResultDto<TaskScheduleDto>>;

  createBackupSchedule(backupSchedule: CreateTaskScheduleDto): Promise<void>;
}

export interface TaskRepositoryInterface {
  searchTasks(type?: string, server?: string, query?: PagingOptions): Promise<QueryResultDto<TaskDto>>;

  createInstantTask(createDto: CreateInstantTaskDto): Promise<void>;

  failTask(type: string, id: number): Promise<void>;
}

export type TaskScheduleDto = {
  id: number;
  type: string;
  targetServerId: number,
  targetServerHostname: string,
  intervalDays: number;
  runAtHour: number;
  runAtMinute: number;
  lastRunDay: Date;
  retries: number;
  createdAt: Date;

  idComposite: string;
};


export type TaskDto = {
  id: number;
  type: string;
  status: number;
  targetServerId: number,
  targetServerHostname: string,
  createdAt: Date;

  idComposite: string;
};

export type CreateBackupServerDto = {
  serverName: string;
};

export type BackupServerId = number;
export type BackupServerDto = {
  id: BackupServerId;
  serverName: string;
};

export type PagingOptions = {
  page: number;
  pageSize: number;
};

export type SearchOptions = {
  search?: string;
}

export type PagingWithSearchOptions = PagingOptions & SearchOptions;

export interface BackupServerRepositoryInterface {
  createBackupServer(server: CreateBackupServerDto): Promise<BackupServerDto>;

  getBackupServer(id: BackupServerId): Promise<BackupServerDto | null>;

  searchBackupServers(query: PagingWithSearchOptions): Promise<QueryResultDto<BackupServerDto>>;

  updateBackupServer(server: BackupServerDto): Promise<void>;

  deleteBackupServer(id: BackupServerId): Promise<void>;

  pruneBackupRepoInstant(data: CreateInstantPruneRepositoryDto): Promise<void>;

  pruneBackupRepoScheduled(data: CreateSchedulePruneRepositoryDto): Promise<void>;

  forgetBackupRepoInstant(data: CreateInstantForgetRepositoryDto): Promise<void>;

  forgetBackupRepoScheduled(data: CreateScheduledForgetRepositoryDto): Promise<void>;
}

export type BackupLogId = number;

export enum LogType {
  INFO = "info",
  ERROR = "error",
  WARNING = "warning"
}

export type BackupLogDto = {
  id: BackupLogId;
  backupId: BackupId;
  date: Date;
  type: LogType;
  log: string;
};

export enum TargetType {
  WEBSITE = 0,
  DATABASE = 1,
}

export type FailedConfigCollectionLogId = number;
export type FailedConfigCollectionLogDto = {
  id: FailedConfigCollectionLogId;
  date: Date;
  targetType: TargetType;
  websiteId: number;
  message: string;
};

export interface BackupLogRepositoryInterface {
  searchBackupLogs(query: PagingOptions): Promise<QueryResultDto<BackupLogDto>>;

  searchFailedConfigCollectionLogs(query: PagingOptions): Promise<QueryResultDto<FailedConfigCollectionLogDto>>;
}

export enum BackupCreationType {
  Instant = 'instant',
  Scheduled = 'scheduled',
}

export enum ConfigResetActionName {
  RESET_DATABASE = 'reset_database',
  RESET_DNS = 'reset_dns',
  RESET_FTP = 'reset_ftp',
  RESET_MAIL = 'reset_mail',
  RESET_SSL = 'reset_ssl',
  RESET_SYSTEM_USER = 'reset_system_user',
  RESET_VHOST = 'reset_vhost',
}

export enum ConfigResetStatus {
  PENDING = 'pending',
  DONE = 'done',
  FAILED = 'failed',
}

export enum ConfigResetActionStatus {
  PENDING = 'pending',
  IN_PROGRESS = 'in progress',
  FAILED = 'failed',
  DONE = 'done',
  CANCELLED = 'cancelled',
}

export type ConfigResetId = number;
export type ConfigResetActionId = number;

export type ConfigResetActionDto<TConfig = unknown> = {
  id: ConfigResetActionId;
  action: ConfigResetActionName;
  parentActionIds: ConfigResetActionId[] | null;
  status: ConfigResetActionStatus;
  config: TConfig;
  configResetId: ConfigResetId;
  logs: ConfigResetActionLogDto[];
};

export type ConfigResetActionLogDto = {
  id: number;
  actionId: ConfigResetActionId;
  log: string;
  createdAt: Date;
};

export type ConfigResetSummaryDto = {
  id: ConfigResetId;
  websiteId: number;
  status: ConfigResetStatus;
  createdAt: Date;
  type: BackupRestoreType,
  isMain: boolean,
  groupId: number,
  restoreTaskId: number,
  backupTargetId: number,
};

export type ConfigResetDto = {
  id: ConfigResetId;
  websiteId: number;
  status: ConfigResetStatus;
  createdAt: Date;
  actions: ConfigResetActionDto[];
};

export type ConfigResetListDto = {
  websiteRestores: ConfigResetDto[]
};

export interface ConfigResetRepositoryInterface {
  getConfigReset(id: ConfigResetId): Promise<ConfigResetDto | null>;

  getConfigResetByTask(id: RestoreTaskId): Promise<ConfigResetListDto>;

  searchConfigResets(query: PagingOptions): Promise<QueryResultDto<ConfigResetSummaryDto>>;

  searchConfigResetActionLogs(query: PagingOptions): Promise<QueryResultDto<ConfigResetActionLogDto>>;

  deleteConfigReset(id: ConfigResetId): Promise<void>;

  deleteConfigResetAction(id: ConfigResetActionId): Promise<void>;

  deleteConfigResetActionLog(id: number): Promise<void>;
}

export type CreateForgetScheduleDto = {
  backupServerId: BackupServerId;
  intervalDays: number;
  runAtHour: number;
  runAtMinute: number;
  retentionDays: number;
  retries: number;
};

export interface ForgetServerRepositoryInterface {
  createScheduleForgetServer(forgetSchedule: CreateForgetScheduleDto): Promise<void>;
}


export enum LockReason {
  LOCK_REASON_UNSPECIFIED = 0,
  LOCK_REASON_FORGET = 1,
  LOCK_REASON_PRUNE = 2,
  LOCK_REASON_BACKUP = 3,
  LOCK_REASON_RESTORE = 4,
  LOCK_REASON_MANUAL = 5,
  UNRECOGNIZED = -1,
}

export enum LockType {
  LOCK_TYPE_UNSPECIFIED = 0,
  LOCK_TYPE_LOCK_ALL = 1,
  LOCK_TYPE_ALLOW_RESTORE = 2,
  UNRECOGNIZED = -1,
}

export type RepoLockId = number;

export type WebsiteLock = {
  id: number;
  websiteId: number;
  createdAt: Date;
  ttl: Date;
}

export type RepoLocksDto = {
  id: number;
  backupRepositoryId: RepoLockId;
  backupServerId: RepoLockId;
  type: LockType;
  ttl: Date;
  createdAt: Date;
  websiteRestoreLocks: WebsiteLock[];
};


export interface RepoLocksRepositoryInterface {
  searchReposLocks(request: PagingOptions): Promise<QueryResultDto<RepoLocksDto>>;

  getRepoLockById(repoLockId: number): Promise<RepoLocksDto>;

  toggleLockState(repoId: number, newState: boolean, type: LockType, reason?: LockReason): Promise<void>;

  deleteRepoLock(repoLockId: number): Promise<void>;
}
